import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { CaretDownIcon, ExitIcon } from '@radix-ui/react-icons';
import { Flex, Box, Text } from '@radix-ui/themes';
import StorageImage from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/storage-image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/fasttrac/contexts/use-tenant-context';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { Button } from '@trashlab/sitesapp/src/organizations/fasttrac/template/components/shared/button';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import {
  IconRentalCommercial,
  IconRentalResidential,
  IconRentalContractor,
  IconRentalToilet,
} from '@trashlab/sitesapp/src/organizations/fasttrac/template/components/icons/icons';
import { Link } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/link';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/fasttrac/lib/utils/get-variant-value';
import { Image } from '@trashlab/sitesapp/src/organizations/fasttrac/components/shared/image';
import { useFilteredSkus } from '@trashlab/sitesapp/src/organizations/fasttrac/hooks/use-filtered-skus';

export const Nav = () => {
  const router = useRouter();
  const { website } = useTenantContext();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  const { containerTypeOptions, getTypeDescription } = useFilteredSkus(
    website?.organization.id ?? '',
    '',
  );

  const getTypeIcon = (typeValue: string) => {
    switch (typeValue) {
      case 'residential':
        return <IconRentalResidential />;
      case 'commercial':
        return <IconRentalCommercial />;
      case 'rolloff':
        return <IconRentalContractor />;
      case 'portableToilet':
        return <IconRentalToilet />;
      default:
        return <IconRentalResidential />;
    }
  };

  const NAV_ITEMS = [
    {
      id: 'service-areas',
      label: 'Service area',
      href: '/service-areas',
      title: 'View our service areas and coverage',
      ariaLabel: 'Navigate to service areas page to see where we operate',
    },
    {
      id: 'faq',
      label: 'Dumpster FAQ',
      href: '/dumpster-faq',
      title: 'Frequently asked questions about dumpster rentals',
      ariaLabel:
        'View frequently asked questions about our dumpster rental services',
    },
    {
      id: 'about',
      label: 'About us',
      href: '/about',
      title: 'Learn more about our company',
      ariaLabel: 'Learn more about our dumpster rental company and services',
    },
  ];

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (!isMobileMenuOpen) return;

      const target = event.target as Node;

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(target) &&
        mobileMenuButtonRef.current &&
        !mobileMenuButtonRef.current.contains(target)
      ) {
        setIsMobileMenuOpen(false);
        setIsMobileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMobileMenuOpen]);

  const renderDumpsterTypes = () => (
    <Flex direction="column" className="w-full" gap="0">
      {containerTypeOptions.map((type) => (
        <NavigationMenu.Link key={type.value} asChild className="h-full">
          <Button
            variant="soft"
            size="large"
            leftIcon={getTypeIcon(type.value)}
            className="w-full text-left xl:py-2 py-4"
            title={`View ${type.label} rental options`}
            aria-label={`Browse our ${
              type.label
            } rental services - ${getTypeDescription(type.value)}`}
            onClick={async () => {
              await router.navigate(type.href);
              await router.invalidate();
              setIsMobileMenuOpen(false);
              setIsMobileDropdownOpen(false);
            }}
          >
            <Flex direction="column" align="start" className="w-full">
              <Text className="font-semibold xl:text-base text-lg sm:text-xl w-full">
                {type.label}
              </Text>
              <Text
                size={{ initial: '3', xl: '2' }}
                className="font-normal whitespace-normal line-clamp-2 text-gray-600 text-left w-full"
              >
                {getTypeDescription(type.value)}
              </Text>
            </Flex>
          </Button>
        </NavigationMenu.Link>
      ))}
    </Flex>
  );

  const renderNavItems = () => {
    return NAV_ITEMS.map((item) => (
      <NavigationMenu.Item key={item.id}>
        <NavigationMenu.Link asChild>
          <Link
            href={item.href}
            className="inline-flex items-center justify-center px-4 py-1 text-base font-[600] text-[#111827] min-h-[42px] hover:bg-[var(--background-color-light)] hover:text-[var(--primary-color)] rounded-md transition-all text-nowrap"
            title={item.title}
            aria-label={item.ariaLabel}
            onClick={() => {
              setIsMobileMenuOpen(false);
              setIsMobileDropdownOpen(false);
            }}
          >
            {item.label}
          </Link>
        </NavigationMenu.Link>
      </NavigationMenu.Item>
    ));
  };

  const renderCustomerService = () =>
    website?.organization.billingPhoneNumber && (
      <Button
        variant="soft"
        className="text-left"
        title="Call customer service"
        aria-label="Call customer service"
        onClick={() => {
          window.open(`tel:${website?.organization.billingPhoneNumber}`, '_blank');
        }}>
        <Flex direction="column" align="start" className="w-full">
          <Text size="2" weight="bold" className="text-nowrap">
            CUSTOMER SERVICE
          </Text>
          <Text size="3" weight="bold" className="text-[var(--primary-color)]">
            {parsePhoneNumberFromString(
              website?.organization.billingPhoneNumber,
            )?.formatNational() ?? ''}
          </Text>
        </Flex>
      </Button>
    );

  const logoImage = useVariantValue('LOGO_IMAGE', '');

  return (
    <Flex
      justify="between"
      align="center"
      className="bg-white py-2 px-4 md:px-8 border-b border-gray-200 shadow-xl sticky top-0 z-30"
    >
      <Box data-variable="LOGO_IMAGE" className="flex-shrink-0">
        <Link
          href="/"
          title="Return to homepage"
          aria-label="Return to homepage"
          className="block"
        >
          {logoImage ? (
            <Image
              src={logoImage}
              alt="logo"
              className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto object-contain max-w-[200px] md:max-w-[280px]"
            />
          ) : website?.organization?.logoPath ? (
            <StorageImage
              alt="logo"
              bucket="org-logos"
              logoPath={website?.organization?.logoPath}
              fit="contain"
              className="h-12 sm:h-14 md:h-16 lg:h-20 w-auto max-w-[200px] md:max-w-[280px]"
            />
          ) : (
            <Text
              size="7"
              weight="bold"
              className="text-[var(--primary-color)]"
            >
              {website?.organization?.name}
            </Text>
          )}
        </Link>
      </Box>

      <NavigationMenu.Root className="relative hidden mx-2 xl:block z-50">
        <NavigationMenu.List className="flex items-center gap-1 list-none m-0 p-0">
          <NavigationMenu.Item>
            <NavigationMenu.Link asChild>
              <Link
                href="/"
                className="inline-flex items-center justify-center px-4 py-1 text-base font-[600] text-[#111827] min-h-[42px] hover:bg-[var(--background-color-light)] hover:text-[var(--primary-color)] rounded-md transition-all text-nowrap"
                title="Return to homepage"
                aria-label="Return to homepage"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setIsMobileDropdownOpen(false);
                }}
              >
                Home
              </Link>
            </NavigationMenu.Link>
          </NavigationMenu.Item>
          <NavigationMenu.Item>
            <NavigationMenu.Trigger asChild>
              <Button
                variant="soft"
                rightIcon={<CaretDownIcon />}
                className="text-base"
                title="View services"
                aria-label="Open menu to select service types"
              >
                Services
              </Button>
            </NavigationMenu.Trigger>
            <NavigationMenu.Content className="absolute top-4 left-0 w-[400px] bg-white rounded-md shadow-lg z-[99999] no-animate">
              {renderDumpsterTypes()}
            </NavigationMenu.Content>
          </NavigationMenu.Item>
          {renderNavItems()}
        </NavigationMenu.List>
        <NavigationMenu.Viewport className="absolute top-full" />
      </NavigationMenu.Root>

      <Box className="z-50">
        <Flex align="center" gap="4" className="ml-auto">
          <Box className="hidden xl:block">{renderCustomerService()}</Box>

          <Box className="hidden xl:block">
            <Button
              variant="light"
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsMobileDropdownOpen(false);
              }}
              href={`https://app.trashlab.com/customer-portal/login?orgId=${website?.organization.id}`}
              title="Manage my account"
              aria-label="Manage my account"
              leftIcon={<ExitIcon className="w-6 h-6" />}
            >
              Manage my account
            </Button>
          </Box>

          <Flex align="center" gap="4" className="xl:hidden">
            {renderCustomerService()}

            <Button
              variant="soft"
              onClick={() => {
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              isSelected={isMobileMenuOpen}
              ref={mobileMenuButtonRef}
              title={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-label={
                isMobileMenuOpen
                  ? 'Close navigation menu'
                  : 'Open navigation menu'
              }
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </Button>
          </Flex>
        </Flex>

        <Box
          ref={mobileMenuRef}
          className={`absolute top-full right-0 bg-white shadow-lg p-5 z-[99999] xl:hidden min-w-[320px] max-w-[100vw] w-[95vw] sm:w-[400px] ${
            isMobileMenuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <NavigationMenu.Root>
            <NavigationMenu.List className="flex flex-col w-full">
              <Flex direction="column" gap="4">
                <NavigationMenu.Item className="w-full">
                  <Box className="w-full">
                    <Button
                      variant="soft"
                      rightIcon={<CaretDownIcon />}
                      onClick={() => {
                        setIsMobileDropdownOpen(!isMobileDropdownOpen);
                      }}
                      isSelected={isMobileDropdownOpen}
                      title="View services"
                      aria-label="Open menu to select service types"
                      className="text-xl font-semibold w-full py-4 text-left justify-start"
                    >
                      Services
                    </Button>
                    <Box
                      className={`w-full overflow-hidden my-3 ${
                        isMobileDropdownOpen ? 'block pl-1' : 'hidden'
                      }`}
                    >
                      {renderDumpsterTypes()}
                    </Box>
                  </Box>
                </NavigationMenu.Item>

                <NavigationMenu.Item>
                  <NavigationMenu.Link asChild>
                    <Link
                      href="/"
                      className="inline-flex items-center justify-center px-4 py-1 text-base font-[600] text-[#111827] min-h-[42px] hover:bg-[var(--background-color-light)] hover:text-[var(--primary-color)] rounded-md transition-all text-nowrap"
                      title="Return to homepage"
                      aria-label="Return to homepage"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setIsMobileDropdownOpen(false);
                      }}
                    >
                      Home
                    </Link>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>

                {renderNavItems()}

                <NavigationMenu.Item className="w-full">
                  <NavigationMenu.Link asChild>
                    <Button
                      className="w-full text-xl font-semibold py-4"
                      variant="light"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsMobileMenuOpen(false);
                        setIsMobileDropdownOpen(false);
                      }}
                      href={`https://app.trashlab.com/customer-portal/login?orgId=${website?.organization.id}`}
                      title="Manage my account"
                      aria-label="Manage my account"
                      leftIcon={<ExitIcon className="w-6 h-6" />}
                    >
                      Manage my account
                    </Button>
                  </NavigationMenu.Link>
                </NavigationMenu.Item>
              </Flex>
            </NavigationMenu.List>
            <NavigationMenu.Viewport />
          </NavigationMenu.Root>
        </Box>
      </Box>
    </Flex>
  );
};

export default Nav;
